import { useEffect, useMemo, useState } from "react"
import { Canvas } from "@react-three/fiber"
import landingData from "./data"
import { CTA } from "./components/CTA"
import { CursorSpotlight } from "./components/CursorSpotlight"
import { HandoffOverlay } from "./components/HandoffOverlay"
import { Header } from "./components/Header"
import { SectionShell } from "./components/SectionShell"
import { StaticFallback } from "./components/StaticFallback"
import { Section1Hero } from "./sections/Section1Hero"
import { Section2Tagline } from "./sections/Section2Tagline"
import { Section3Lattice } from "./sections/Section3Lattice"
import { Section4Terrain } from "./sections/Section4Terrain"
import { Section5Atlas } from "./sections/Section5Atlas"
import { Section6Tour } from "./sections/Section6Tour"
import { Section7Stats } from "./sections/Section7Stats"
import { Section8Footer } from "./sections/Section8Footer"
import { LandingScene } from "./scenes/LandingScene"
import { classifyTier } from "./lib/device-tier"
import { useLandingMotion } from "./lib/use-landing-motion"
import { useScrollProgress } from "./lib/use-scroll-progress"

export default function App() {
  const motion = useScrollProgress()
  const [tier, setTier] = useState<"high" | "medium" | "low">("low")
  const reducedMotion = useMemo(() => {
    if (typeof window === "undefined") return true
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }, [])

  useEffect(() => {
    setTier(classifyTier())
  }, [])

  useEffect(() => {
    document.documentElement.dataset.verusSection = String(motion.sectionIndex)
  }, [motion.sectionIndex])

  useLandingMotion(!reducedMotion)

  const showCanvas = tier !== "low" && !reducedMotion

  return (
    <>
      {showCanvas ? (
        <Canvas
          id="scene"
          camera={{ position: [0, 0.05, 7.2], fov: 48, near: 0.1, far: 100 }}
          dpr={tier === "high" ? [1, 2] : [1, 1.5]}
          gl={{ antialias: true, alpha: true, powerPreference: "high-performance" }}
          style={{ height: "100vh", inset: 0, position: "fixed", width: "100vw", zIndex: 0 }}
        >
          <color attach="background" args={["#020404"]} />
          <LandingScene data={landingData} motion={motion} tier={tier} />
        </Canvas>
      ) : (
        <StaticFallback data={landingData} motion={motion} />
      )}

      <CursorSpotlight enabled={!reducedMotion} />
      <HandoffOverlay />
      <Header />

      <main>
        <SectionShell id="hero" className="hero" section={0}>
          <Section1Hero data={landingData} />
        </SectionShell>
        <SectionShell id="signal" section={1}>
          <Section2Tagline />
        </SectionShell>
        <SectionShell id="lattice" section={2} align="right">
          <Section3Lattice />
        </SectionShell>
        <SectionShell id="terrain" section={3}>
          <Section4Terrain />
        </SectionShell>
        <SectionShell id="atlas" section={4} align="right" variant="method">
          <Section5Atlas data={landingData} />
        </SectionShell>
        <SectionShell id="tour" section={5}>
          <Section6Tour />
        </SectionShell>
        <SectionShell id="coverage" section={6} className="stats-panel">
          <Section7Stats data={landingData} />
        </SectionShell>
        <SectionShell id="footer" section={7} className="final">
          <Section8Footer data={landingData} />
        </SectionShell>
      </main>

      <div className="mobile-terminal-dock">
        <CTA>ENTER TERMINAL</CTA>
      </div>
    </>
  )
}
